import React, { useCallback, useEffect, useState } from 'react';
import StyledModal from '../StyledModal';
import { Badge, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import styled from 'styled-components';
import CreateAppointmentForm from './CreateAppointmentForm';
import i18next from 'i18next';
import { useNavigate } from 'react-router';
import moment from 'moment';
import ConfirmationModal from '../../components/ConfirmationModal';
import 'moment/locale/de';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { AppointmentStatus, IAppointment, ITherapy } from '../../util/types';

type DurationMin = 15 | 30 | 45 | 60;
type CreatePayload = { start: string, end: string, duration: DurationMin };

const AppointmentListWrapper = styled.main`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
    
  .dropdown-item {
    font-size: 1.5rem;
  }
    
  .btn-secondary {
    border: 2px solid #ff695f;
    color: #ff695f;
  }
    
  h1 {
    font-weight: bold;
    font-size: 4rem;
  }
    
  .banner {
    display: flex;
    > div {
      max-width: 480px;
    }
  }
    
  .control-btns {
    white-space: nowrap;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-left: auto;
    .btn {
      margin-bottom: 8px;
    }
  }
    
  .patient-overview {
    display: flex;
    flex-direction: column;
    margin-top: 8px;
    > div {
      &:hover {
        background-color: #ececec;
        cursor: pointer;
        .name {
          font-weight: 700;
        }
      }
      display: flex;
      white-space: nowrap;
      padding: 4px 16px;
      align-items: center;
      background: white;
      &:first-of-type {
        padding-top: 8px;
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
      }
      &:last-of-type {
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
        padding-bottom: 8px;
      }
      .name {
        flex: 1;
        color: #444;
      }
      input {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        height: 39px;
        width: 250px;
      }
    }
  }
    
  @media (max-width: 750px), (max-device-width: 815px) and (-webkit-min-device-pixel-ratio: 3) {
    .banner {
      h1 {
        font-size: 1.8rem;
      }
      flex-direction: column;
      .control-btns {
        align-items: flex-start;
      }
    }
      
    .text-end {
      text-align: left !important;
    }
    
    .patient-overview {
      > div {
        flex-direction: column;
        border-top: 1px solid #eee;
        &:first-of-type {
          border-top: 0;
        }
        .name {
          width: 100%;
          padding: 0;
        }
        .btn-group input {
          width: 100%;
        }
        .btn-secondary {
          margin: 8px auto 8px 0;
        }
      }
    }
  }
`;

const variantByStatus: Record<AppointmentStatus, string> = {
  [AppointmentStatus.PENDING]: 'badge-bg-secondary',
  [AppointmentStatus.CONFIRMED]: 'badge-bg-info',
  [AppointmentStatus.THERAPIST_CANCELLED]: 'badge-bg-danger',
  [AppointmentStatus.PATIENT_CANCELLED]: 'badge-bg-danger',
  [AppointmentStatus.REJECTED]: 'badge-bg-danger',
  [AppointmentStatus.REQUESTED]: 'badge-bg-secondary',
  [AppointmentStatus.COMPLETED]: '',
  [AppointmentStatus.DELETED]: '',
};

const AppointmentList = ({ therapy, isShow, onModalHide, onCreateAppointment, appointmentList }: {
  therapy: ITherapy;
  isShow: boolean;
  onModalHide: (value: boolean) => void;
  onCreateAppointment: (payload: CreatePayload) => Promise<void>;
  appointmentList: IAppointment[];
} ) => {
  const [appointmentId, setAppointmentId] = useState<string>();
  const [comment, setComment] = useState('');
  const [showCancelModel, setShowCancelModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [list, setList] = useState<IAppointment[]>([]);
  const [busy, setBusy] = useState<null | 'cancel' | 'accept' | 'reject'>(null);

  const navigate = useNavigate();
  const patient = therapy?.patient ?? null;
  const { authFetch } = useAuthFetchFromRestAPI();

  useEffect(() => {
    setList(Array.isArray(appointmentList) ? [...appointmentList]: []);
  }, [appointmentList]);

  const patchLocalStatus = useCallback((id: string, status: AppointmentStatus) => {
    setList((prev) => prev.map((it) => (it._id === id ? { ...it, status } : it)));
  }, []);

  const handleCancel = useCallback(async () => {
    if (!appointmentId) {
      return;
    }

    setBusy('cancel');

    try {
      const result = await authFetch(`/api/v1/appointment/therapist/cancel/${appointmentId}`, {
        method: 'POST',
        body: { comment },
      });

      if (!result?.error) {
        patchLocalStatus(appointmentId, AppointmentStatus.THERAPIST_CANCELLED);
        setComment('');
      }
    } catch (error) {
      console.error('Error cancel appointment', error);
    } finally {
      setBusy(null);
      setShowCancelModal(false);
    }
  }, [appointmentId, comment, authFetch, patchLocalStatus]);

  const handleAccept = useCallback(async () => {
    if (!appointmentId) {
      return;
    }

    setBusy('accept');

    try {
      const result = await authFetch(`/api/v1/appointment/therapist/confirm/${appointmentId}`, {
        method: 'POST',
      });

      if (!result?.error) {
        patchLocalStatus(appointmentId, AppointmentStatus.CONFIRMED);
      }
    } catch (error) {
      console.error('Error accept appointment', error);
    } finally {
      setBusy(null);
      setShowAcceptModal(false);
    }
  }, [appointmentId, authFetch, patchLocalStatus]);

  const handleReject = useCallback(async () => {
    if (!appointmentId) {
      return;
    }

    setBusy('reject');

    try {
      const result = await authFetch(`/api/v1/appointment/reject/${appointmentId}`, {
        method: 'POST',
        body: { comment },
      });

      if (!result?.error) {
        patchLocalStatus(appointmentId, AppointmentStatus.REJECTED);
        setComment('');
      }
    } catch (error) {
      console.error('Error reject appointment', error);
    } finally {
      setBusy(null);
      setShowRejectModal(false);
    }
  }, [appointmentId, comment, authFetch, patchLocalStatus]);

  const appointmentStatusBadge = (status: AppointmentStatus) => (
    <Badge className={`float-start badge-status align-top me-3 w-105 ${variantByStatus[status]}`}>
      {i18next.t(`createAppointmentForm.appointmentStatus.${status}`)}
    </Badge>
  );


  return (
    <AppointmentListWrapper>
      <ConfirmationModal
        show={showCancelModel}
        title={i18next.t('createAppointmentForm.deleteModal.title')}
        body={
          <>
            <p>{i18next.t('createAppointmentForm.deleteModal.body')}</p>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={2000}
              value={comment}
              onChange={(event) => setComment(event.target.value.slice(0, 2000))}
            />
            <div className="text-end small mt-1">{comment.length}/2000</div>
          </>
        }
        buttonPositive={i18next.t('createAppointmentForm.labels.delete')}
        buttonNegative={i18next.t('generic.cancel')}
        onPositive={handleCancel}
        onNegative={() => setShowCancelModal(false)}
        disableButton={busy === 'cancel'}
      />

      <ConfirmationModal
        show={showAcceptModal}
        title={i18next.t('createAppointmentForm.acceptModal.title')}
        body={<p>{i18next.t('createAppointmentForm.acceptModal.body')}</p>}
        buttonPositive={i18next.t('createAppointmentForm.labels.accept')}
        buttonNegative={i18next.t('generic.cancel')}
        onPositive={handleAccept}
        onNegative={() => setShowAcceptModal(false)}
        disableButton={busy === 'accept'}
      />

      <ConfirmationModal
        show={showRejectModal}
        title={i18next.t('createAppointmentForm.rejectModal.title')}
        body={
          <>
            <p>{i18next.t('createAppointmentForm.rejectModal.body')}</p>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={2000}
              value={comment}
              onChange={(event) => setComment(event.target.value.slice(0, 2000))}
            />
            <div className="text-end small mt-1">{comment.length}/2000</div>
          </>
        }
        buttonPositive={i18next.t('createAppointmentForm.labels.reject')}
        buttonNegative={i18next.t('generic.cancel')}
        onPositive={handleReject}
        onNegative={() => setShowRejectModal(false)}
        disableButton={busy === 'reject'}
      />

      <StyledModal
        show={isShow}
        onHide={() => onModalHide(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{i18next.t('createAppointmentForm.modalTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CreateAppointmentForm
            therapy={therapy}
            onCreateAppointment={async (payload: CreatePayload) => {
              await onCreateAppointment(payload);
              onModalHide(false);
            }}
          />
        </Modal.Body>
      </StyledModal>

      <div className="patient-overview">
        {list.length > 0 &&
          list.map((appointment) => {
            const start = moment(appointment.startDateTime).local().format('DD-MMM-YYYY HH:mm');
            const end = moment(appointment.endDateTime).local().format('HH:mm');

            const status = appointment.status as AppointmentStatus;

            const canAct =
              status !== AppointmentStatus.REJECTED &&
              status !== AppointmentStatus.THERAPIST_CANCELLED &&
              status !== AppointmentStatus.PATIENT_CANCELLED;

            return (
              <Row key={`request-${appointment._id}`}>
                <Col xl={3} md={4} className="text-wrap">
                  {start} {' - '} {end}
                </Col>

                <Col xl={3} md={4} className="text-wrap">
                  <span className="align-middle fw-bold">
                    {patient?.firstName} {patient?.lastName}
                  </span>
                </Col>

                <Col md={2} className="pt-2 pb-2">
                  {appointmentStatusBadge(status)}
                </Col>

                <Col xl={4} md={4} className="text-end">
                  {canAct && (
                    <>
                      {status === AppointmentStatus.PENDING && (
                        <>
                          <Button
                            className="me-2"
                            onClick={() => {
                              setAppointmentId(appointment._id);
                              setShowAcceptModal(true);
                            }}
                            variant="primary"
                            disabled={busy !== null}
                          >
                            <i className="fas fa-check" aria-hidden="true" /> {i18next.t('generic.approve')}
                          </Button>

                          <Button
                            className="me-2"
                            onClick={() => {
                              setAppointmentId(appointment._id);
                              setShowRejectModal(true);
                            }}
                            variant="secondary"
                            disabled={busy !== null}
                          >
                            <i className="fa fa-times" aria-hidden="true" /> {i18next.t('generic.reject')}
                          </Button>
                        </>
                      )}

                      {status === AppointmentStatus.CONFIRMED && (
                        <>
                          <Button
                            className="me-2"
                            onClick={() => {
                              setAppointmentId(appointment._id);
                              setShowCancelModal(true);
                            }}
                            variant="secondary"
                            disabled={busy !== null}
                          >
                            <i className="fas fa-times" aria-hidden="true" /> {i18next.t('createAppointmentForm.labels.cancel')}
                          </Button>

                          <Button
                            className="me-2"
                            onClick={() => navigate(`/room/${appointment.roomCode}`)}
                            variant="secondary"
                          >
                            {i18next.t('patients.enterRoom')}
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </Col>
              </Row>
            );
          })}
      </div>
    </AppointmentListWrapper>
  );
};

export default AppointmentList;
